import { useState, useRef, useCallback } from 'react';

interface Props {
  images: string[];
  onChange: (images: string[]) => void;
}

export default function PhotoUploader({ images, onChange }: Props) {
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Always reflect latest images in a ref so async callbacks see fresh data
  const imagesRef = useRef<string[]>(images);
  imagesRef.current = images;

  const handleUrlAdd = useCallback(() => {
    const urls = urlInput
      .split(/[\n,]+/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0);
    if (urls.length === 0) return;
    const current = imagesRef.current;
    const next = [...current, ...urls.filter((u) => !current.includes(u))];
    onChange(next);
    setUrlInput('');
  }, [urlInput, onChange]);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    // Read ALL files in parallel, then merge once into a single onChange call
    const promises = imageFiles.map(
      (file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            if (result) resolve(result);
            else reject(new Error('empty result'));
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    );

    Promise.allSettled(promises).then((results) => {
      const newUrls = results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
        .map((r) => r.value);

      if (newUrls.length === 0) return;

      // Read the very latest images at this moment
      const current = imagesRef.current;
      const deduplicated = newUrls.filter((u) => !current.includes(u));
      if (deduplicated.length > 0) {
        onChange([...current, ...deduplicated]);
      }
    });
  }, [onChange]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeImage = (idx: number) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/40'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            // Reset so same files can be re-selected if needed
            e.target.value = '';
          }}
        />
        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-amber-100 rounded-full">
          <i className="ri-upload-cloud-2-line text-amber-500 text-xl"></i>
        </div>
        <p className="text-sm font-medium text-gray-700">Drop photos here or click to upload</p>
        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP — unlimited photos, multiple files supported</p>
      </div>

      {/* URL paste input */}
      <div className="flex gap-2 items-start">
        <textarea
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleUrlAdd();
            }
          }}
          placeholder="Paste photo URLs here — one per line or comma-separated, then click Add"
          rows={2}
          className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400 resize-none"
        />
        <button
          type="button"
          onClick={handleUrlAdd}
          disabled={!urlInput.trim()}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
        >
          Add
        </button>
      </div>

      {/* Preview grid */}
      {images.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 mb-2">
            {images.length} photo{images.length !== 1 ? 's' : ''} — first photo is the cover
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((src, idx) => (
              <div key={`${src}-${idx}`} className="relative group rounded-md overflow-hidden border border-gray-100 bg-gray-50">
                <div className="w-full h-24">
                  <img src={src} alt={`photo ${idx + 1}`} className="w-full h-full object-cover object-top" />
                </div>
                {idx === 0 && (
                  <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-semibold rounded">
                    Cover
                  </span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx - 1); }}
                    disabled={idx === 0}
                    className="w-7 h-7 flex items-center justify-center bg-white/90 rounded text-gray-700 disabled:opacity-30 cursor-pointer hover:bg-white transition-colors"
                    title="Move left"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                    className="w-7 h-7 flex items-center justify-center bg-red-500 rounded text-white cursor-pointer hover:bg-red-600 transition-colors"
                    title="Remove"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx + 1); }}
                    disabled={idx === images.length - 1}
                    className="w-7 h-7 flex items-center justify-center bg-white/90 rounded text-gray-700 disabled:opacity-30 cursor-pointer hover:bg-white transition-colors"
                    title="Move right"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
